<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <b-link class="brand-logo">
        <img src="../../../public/logo.png" alt="logo" height="50" />
      </b-link>

      <b-col lg="8" class="d-none d-lg-flex align-items-center p-5">
        <div
          class="w-100 d-lg-flex align-items-center justify-content-center px-5"
        >
          <b-img fluid :src="imgUrl" alt="OTP Verification" />
        </div>
      </b-col>

      <b-col lg="4" class="d-flex align-items-center auth-bg px-2 p-lg-5">
        <b-col sm="8" md="6" lg="12" class="px-xl-2 mx-auto">
          <b-card-title class="mb-1 font-weight-bold" title-tag="h2">
            OTP Verification 🔐
          </b-card-title>
          <b-card-text class="mb-2">
            {{ message }}
          </b-card-text>

          <b-alert
            v-if="showSuccessMessage"
            show
            variant="success"
            class="mb-2"
          >
            <div class="alert-body">OTP sent successfully</div>
          </b-alert>

          <validation-observer ref="otpForm" #default="{ invalid }">
            <b-form class="auth-otp-form mt-2" @submit.prevent="verifyOtp">
              <b-form-group label="Enter OTP Code" label-for="otp-code">
                <div
                  class="otp-input-container d-flex justify-content-between mb-1"
                >
                  <b-form-input
                    v-for="(digit, index) in otpDigits"
                    :key="index"
                    :ref="`otpInput${index}`"
                    v-model="otpDigits[index]"
                    type="text"
                    maxlength="1"
                    class="otp-input text-center"
                    @input="handleInput(index, $event)"
                    @keydown="handleKeydown(index, $event)"
                  />
                </div>
              </b-form-group>

              <b-button
                type="submit"
                variant="primary"
                block
                :disabled="!isOtpComplete"
              >
                Verify
              </b-button>
            </b-form>
          </validation-observer>

          <p class="text-center mt-2">
            <small class="text-muted">
              Didn't receive the OTP?
              <b-link
                v-if="timeLeft === 0"
                @click="resendOtp"
                class="text-primary"
              >
                Resend
              </b-link>
              <span v-else class="text-secondary">
                Resend in {{ formattedTime }}
              </span>
            </small>
          </p>

          <p class="text-center mt-2">
            <b-link :to="{ name: 'login' }">
              <small>Back to Login</small>
            </b-link>
          </p>
        </b-col>
      </b-col>

      <b-overlay :show="isLoading" no-wrap rounded="sm">
        <template #overlay>
          <div id="loading-bg">
            <div class="loading-logo">
              <img src="../../../public/logo.png" alt="Please Wait..." />
            </div>
          </div>
        </template>
      </b-overlay>
    </b-row>
  </div>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import {
  BRow,
  BCol,
  BLink,
  BFormGroup,
  BFormInput,
  BCardText,
  BCardTitle,
  BImg,
  BForm,
  BButton,
  BOverlay,
  BAlert,
} from "bootstrap-vue";
import axios from "@axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { setAuthenticated } from "@/router";

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    BOverlay,
    BAlert,
    ValidationObserver,
  },
  data() {
    return {
      otpDigits: ["", "", "", "", "", ""],
      tempToken: "",
      message: "",
      form: {
        username: "",
        password: "",
      },
      isLoading: false,
      showSuccessMessage: true,
      timeLeft: 300, // 5 minutes in seconds
      timerInterval: null,
      sideImg: require("@/assets/images/pages/new-banner.svg"),
    };
  },
  computed: {
    imgUrl() {
      return this.sideImg;
    },
    isOtpComplete() {
      return this.otpDigits.every((digit) => digit !== "");
    },
    formattedTime() {
      const minutes = Math.floor(this.timeLeft / 60);
      const seconds = this.timeLeft % 60;
      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
        2,
        "0",
      )}`;
    },
  },
  mounted() {
    if (this.$route.params.form) {
      this.form = this.$route.params.form;
      this.tempToken = this.$route.params.tempToken;
      this.message =
        this.$route.params.message ||
        "Enter the OTP code sent to your email and phone.";
      this.startTimer();
      this.$refs.otpInput0[0].focus();
    } else {
      this.$router.push({ name: "login" });
    }
  },
  beforeDestroy() {
    this.clearTimer();
  },
  methods: {
    handleInput(index, value) {
      if (!/^\d*$/.test(value)) {
        this.otpDigits[index] = "";
        return;
      }

      if (value && index < 5) {
        this.$refs[`otpInput${index + 1}`][0].focus();
      }
    },
    handleKeydown(index, event) {
      if (event.key === "Backspace" && !this.otpDigits[index] && index > 0) {
        this.$refs[`otpInput${index - 1}`][0].focus();
      }
    },
    startTimer() {
      this.clearTimer();
      this.timerInterval = setInterval(() => {
        if (this.timeLeft > 0) {
          this.timeLeft--;
        } else {
          this.clearTimer();
        }
      }, 1000);
    },
    clearTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },
    verifyOtp() {
      if (!this.isOtpComplete) return;

      this.isLoading = true;
      const otpCode = this.otpDigits.join("");

      axios
        .post("/User/api/V1.0/Account/Verify2FA", {
          tempToken: this.tempToken,
          otpCode: otpCode,
        })
        .then((response) => {
          this.$store.dispatch("permission/handlePermission", response);
          setAuthenticated();
          this.isLoading = false;
          window.location.href = "/";
        })
        .catch((error) => {
          this.isLoading = false;
          this.otpDigits = ["", "", "", "", "", ""];
          this.$refs.otpInput0[0].focus();
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              icon: "BellIcon",
              variant: "danger",
              text: error.data?.message || "Invalid OTP code",
            },
          });
        });
    },
    resendOtp() {
      this.isLoading = true;
      axios
        .get("/User/api/V1.0/Account/ResendOTP")
        .then((response) => {
          this.tempToken = response.tempToken;
          this.isLoading = false;
          this.showSuccessMessage = true;
          this.timeLeft = 300;
          this.otpDigits = ["", "", "", "", "", ""];
          this.startTimer();
          this.$refs.otpInput0[0].focus();

          setTimeout(() => {
            this.showSuccessMessage = false;
          }, 5000);

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Success",
              icon: "CheckIcon",
              variant: "success",
              text: response.data?.message || "OTP sent successfully",
            },
          });
        })
        .catch((error) => {
          this.isLoading = false;
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              icon: "BellIcon",
              variant: "danger",
              text: error.data?.message || "Failed to resend OTP",
            },
          });
        });
    },
  },
};
</script>

<style scoped>
.otp-input-container {
  gap: 0.5rem;
}

.otp-input {
  width: 3rem;
  height: 3rem;
  font-size: 1.5rem;
  font-weight: bold;
  padding: 0;
}

.otp-input:focus {
  border-color: #7367f0;
  box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
}
</style>
